import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle, Clock, AlertCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useUserRoles } from "@/hooks/useUserRoles";

interface WorkflowStage {
  id: string;
  candidate_id: string;
  stage_name: string;
  status: string;
  started_at: string;
  completed_at: string;
  candidate_name?: string;
}

interface CandidateWorkflow {
  candidate_id: string;
  candidate_name: string;
  stages: WorkflowStage[];
  current_stage: string;
  progress_percentage: number;
}

export function WorkflowOverview() {
  const [workflows, setWorkflows] = useState<CandidateWorkflow[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const { isSuperAdmin, isAdmin, isUnassignedAdmin, adminTeamIds } = useUserRoles();

  const standardStages = [
    'application_review',
    'first_interview', 
    'first_interview_evaluation',
    'second_interview',
    'occupational_testing',
    'final_decision',
    'onboarding'
  ];

  const stageDisplayNames: Record<string, string> = {
    'application_review': 'Application Review',
    'first_interview': 'First Interview',
    'first_interview_evaluation': 'First Interview Evaluation',
    'second_interview': 'Second Interview',
    'occupational_testing': 'Occupational Testing',
    'final_decision': 'Final Decision',
    'onboarding': 'Onboarding'
  };

  useEffect(() => {
    fetchWorkflowData();
    
    // Set up realtime subscriptions for all relevant tables
    const workflowChannel = supabase
      .channel(`workflow-changes-${Date.now()}`) // Make channel name unique
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'hr_workflow_stages'
        },
        (payload) => {
          console.log('Workflow stage change detected:', payload);
          fetchWorkflowData();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'interviews'
        },
        (payload) => {
          console.log('Interview change detected:', payload);
          fetchWorkflowData();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'test_results'
        },
        (payload) => {
          console.log('Test result change detected:', payload);
          fetchWorkflowData();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'candidates'
        },
        (payload) => {
          console.log('Candidate change detected:', payload);
          fetchWorkflowData();
        }
      )
      .subscribe();

    // Cleanup subscription on unmount
    return () => {
      supabase.removeChannel(workflowChannel);
    };
  }, []);

  const fetchWorkflowData = async () => {
    try {
      // Unassigned admins see no candidates
      if (isUnassignedAdmin) {
        setWorkflows([]);
        setLoading(false);
        return;
      }

      // Fetch candidates based on permissions
      let candidatesQuery = supabase
        .from('candidates')
        .select('id, full_name, status, created_by')
        .neq('status', 'rejected')
        .neq('status', 'hired');

      // Team admins see candidates they created OR from managers in their teams
      if (isAdmin && !isSuperAdmin && adminTeamIds.length > 0) {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('Not authenticated');

        // Get all managers in the admin's teams
        const { data: teamManagers } = await supabase
          .from('manager_teams')
          .select('manager_id')
          .in('team_id', adminTeamIds);

        const managerIds = teamManagers?.map(m => m.manager_id) || [];
        const allowedCreatorIds = [user.id, ...managerIds];

        candidatesQuery = candidatesQuery.in('created_by', allowedCreatorIds);
      }

      const { data: candidates, error: candidatesError } = await candidatesQuery;

      if (candidatesError) throw candidatesError;

      const { data: stages, error: stagesError } = await supabase
        .from('hr_workflow_stages')
        .select('*')
        .order('created_at', { ascending: true });

      if (stagesError) throw stagesError;

      // Group stages by candidate
      const candidateWorkflows: CandidateWorkflow[] = candidates?.map(candidate => {
        const candidateStages = stages?.filter(stage => stage.candidate_id === candidate.id) || [];
        
        // Get unique stages only (in case of duplicates)
        const uniqueStages = candidateStages.reduce((acc, stage) => {
          const existingIndex = acc.findIndex(s => s.stage_name === stage.stage_name);
          if (existingIndex >= 0) {
            // Keep the one with the latest status (completed > in_progress > pending)
            const existing = acc[existingIndex];
            if (stage.status === 'completed' || (existing.status === 'pending' && stage.status === 'in_progress')) {
              acc[existingIndex] = stage;
            }
          } else {
            acc.push(stage);
          }
          return acc;
        }, [] as any[]);
        
        const completedStages = uniqueStages.filter(stage => stage.status === 'completed').length;
        console.log(`Candidate ${candidate.full_name}: ${completedStages} completed stages out of ${standardStages.length} total stages`);
        console.log('Unique candidate stages:', uniqueStages.map(s => ({ name: s.stage_name, status: s.status })));
        
        const progress = (completedStages / standardStages.length) * 100;
        
        // Find the stage that has "in_progress" status
        let currentStageIndex = 0;
        let foundInProgressStage = false;
        
        console.log(`Candidate ${candidate.full_name} stages:`, uniqueStages.map(s => ({ stage: s.stage_name, status: s.status })));
        
        for (let i = 0; i < standardStages.length; i++) {
          const stageName = standardStages[i];
          let stageData;
          
          // Handle occupational_testing mapping - check if it exists directly
          if (stageName === 'occupational_testing') {
            stageData = uniqueStages.find(s => s.stage_name === 'occupational_testing');
          } else {
            stageData = uniqueStages.find(s => s.stage_name === stageName);
          }
          
          if (stageData && stageData.status === 'in_progress') {
            console.log(`Found in_progress stage for ${candidate.full_name}: ${stageName}`);
            currentStageIndex = i;
            foundInProgressStage = true;
            break;
          }
        }
        
        // If no in_progress stage found, use the next stage after the last completed one
        if (!foundInProgressStage) {
          for (let i = 0; i < standardStages.length; i++) {
            const stageName = standardStages[i];
            
            const stageData = uniqueStages.find(s => s.stage_name === stageName);
            if (!stageData || stageData.status !== 'completed') {
              currentStageIndex = i;
              break;
            }
            // If all stages are completed, stay at the last stage
            if (i === standardStages.length - 1) {
              currentStageIndex = i;
            }
          }
        }
        
        const currentStage = standardStages[currentStageIndex];

        return {
          candidate_id: candidate.id,
          candidate_name: candidate.full_name,
          stages: uniqueStages.map(stage => ({
            ...stage,
            candidate_name: candidate.full_name
          })),
          current_stage: currentStage,
          progress_percentage: Math.round(progress)
        };
      }) || [];

      setWorkflows(candidateWorkflows);
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to fetch workflow data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'in_progress': return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'pending': return <AlertCircle className="h-4 w-4 text-gray-400" />;
      default: return <AlertCircle className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800';
      case 'pending': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getCandidatesForStage = (stageName: string) => {
    return workflows.filter(workflow => workflow.current_stage === stageName);
  };

  const getLastName = (fullName: string) => {
    const nameParts = fullName.trim().split(' ');
    return nameParts[nameParts.length - 1];
  };

  if (loading) {
    return <div className="text-center py-8">Loading workflow data...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Hiring Workflow Overview</h2>
        <Button variant="outline">
          Configure Workflow
        </Button>
      </div>

      {/* Standard Hiring Process with Candidates */}
      <Card>
        <CardHeader>
          <CardTitle>Standard Hiring Process</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex overflow-x-auto gap-4 pb-4"
               style={{ scrollbarWidth: 'thin' }}>
            {standardStages.map((stage, index) => {
              const candidatesInStage = getCandidatesForStage(stage);
              
              return (
                <div key={stage} className="min-h-[200px] border rounded-lg p-4 bg-muted/20 w-full min-w-[180px]">
                  {/* Stage Header */}
                  <div className="mb-4">
                    <div className="flex items-center justify-center">
                      <div className="bg-primary text-primary-foreground px-3 py-2 rounded-lg text-xs font-medium text-center min-h-[60px] flex items-center w-full">
                        <span className="break-words text-center w-full leading-tight">
                          {stageDisplayNames[stage] || stage}
                        </span>
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground text-center mt-2">
                      {candidatesInStage.length} candidate{candidatesInStage.length !== 1 ? 's' : ''}
                    </div>
                  </div>

                  {/* Candidates in this stage */}
                  <div className="space-y-2">
                    {candidatesInStage.map((workflow) => (
                      <div 
                        key={workflow.candidate_id} 
                        className="bg-card border rounded-md p-3 hover:bg-accent/50 transition-colors cursor-pointer"
                      >
                        <div className="font-medium text-sm mb-1">
                          {getLastName(workflow.candidate_name)}
                        </div>
                        <div className="flex items-center justify-between">
                          <Badge variant="outline" className="text-xs">
                            {workflow.progress_percentage}%
                          </Badge>
                          <div className="flex items-center space-x-1">
                            {getStatusIcon(
                              workflow.stages.find(s => s.stage_name === stage)?.status || 'pending'
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Arrow to next stage */}
                  {index < standardStages.length - 1 && (
                    <div className="hidden lg:flex items-center justify-center mt-4">
                      <ArrowRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {workflows.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">
          No active candidates in the hiring pipeline
        </div>
      )}
    </div>
  );
}